"use client";

import { ArrowUpRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ReadyToBuildYourFuture() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const embers = [{
    left: "8%",
    delay: "0s",
    duration: "7s",
    size: "w-1 h-1"
  }, {
    left: "18%",
    delay: "1.2s",
    duration: "9s",
    size: "w-1.5 h-1.5"
  }, {
    left: "28%",
    delay: "0.6s",
    duration: "8s",
    size: "w-1 h-1"
  }, {
    left: "38%",
    delay: "2.1s",
    duration: "10s",
    size: "w-0.5 h-0.5"
  }, {
    left: "48%",
    delay: "0.3s",
    duration: "7.5s",
    size: "w-1 h-1"
  }, {
    left: "58%",
    delay: "1.8s",
    duration: "9.5s",
    size: "w-1.5 h-1.5"
  }, {
    left: "68%",
    delay: "0.9s",
    duration: "8.5s",
    size: "w-1 h-1"
  }, {
    left: "78%",
    delay: "2.4s",
    duration: "7s",
    size: "w-0.5 h-0.5"
  }, {
    left: "88%",
    delay: "1.5s",
    duration: "9s",
    size: "w-1 h-1"
  }, {
    left: "94%",
    delay: "0.4s",
    duration: "8s",
    size: "w-1.5 h-1.5"
  }, {
    left: "14%",
    delay: "3.2s",
    duration: "10s",
    size: "w-1 h-1"
  }, {
    left: "62%",
    delay: "2.7s",
    duration: "7.5s",
    size: "w-0.5 h-0.5"
  }];
  return <section data-section-id="3715" id="ready-to-build-your-future" className="dark relative bg-background text-foreground overflow-hidden w-full min-h-[80vh] flex items-center justify-center">
      {}
      <div className="absolute inset-0 pointer-events-none" style={{
      background: "radial-gradient(ellipse 80% 60% at 50% 110%, hsla(200, 70%, 55%, 0.35) 0%, hsla(330, 80%, 92%, 0.12) 30%, transparent 65%)"
    }} aria-hidden="true" />

      {}
      <div className="absolute left-0 top-1/3 bottom-0 w-1/3 pointer-events-none opacity-60" style={{
      background: "radial-gradient(ellipse at 0% 80%, hsla(200, 70%, 55%, 0.18) 0%, transparent 60%)"
    }} aria-hidden="true" />

      {}
      <div className="absolute right-0 top-1/3 bottom-0 w-1/3 pointer-events-none opacity-60" style={{
      background: "radial-gradient(ellipse at 100% 80%, hsla(330, 80%, 92%, 0.15) 0%, transparent 60%)"
    }} aria-hidden="true" />

      {}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        {embers.map((ember, i) => <span key={i} data-index={i} className={`absolute ${ember.size} rounded-lg bg-primary block`} style={{
        left: ember.left,
        bottom: "-10px",
        animation: `spark-rise ${ember.duration} ease-out ${ember.delay} infinite`,
        boxShadow: "0 0 12px hsla(200, 70%, 55%, 0.8), 0 0 24px hsla(330, 80%, 92%, 0.4)"
      }} />)}
      </div>

      <style>{`
        @keyframes spark-rise {
          0% {
            transform: translateY(0) translateX(0) scale(1);
            opacity: 0;
          }
          10% { opacity: 1; }
          50% {
            transform: translateY(-40vh) translateX(20px) scale(0.8);
            opacity: 0.8;
          }
          100% {
            transform: translateY(-85vh) translateX(-15px) scale(0.2);
            opacity: 0;
          }
        }
        @keyframes ember-pulse {
          0%, 100% {
            opacity: 1;
            box-shadow: 0 0 0 0 hsla(200, 70%, 55%, 0.7);
          }
          50% {
            opacity: 0.6;
            box-shadow: 0 0 0 8px hsla(200, 70%, 55%, 0);
          }
        }
      `}</style>

      {}
      <div ref={ref} className={`relative z-10 w-full px-6 sm:px-10 lg:px-16 py-24 lg:py-32 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="max-w-6xl mx-auto flex flex-col items-center text-center">
          {}
          <div className="font-mono text-xs sm:text-sm tracking-[0.3em] uppercase text-primary mb-10 sm:mb-14">
            06 &mdash; Let&apos;s Grow
          </div>

          {}
          <h2 className="font-default font-light leading-[0.9] tracking-tight text-foreground mb-10 sm:mb-12" style={{
          fontSize: "clamp(3.5rem, 14vw, 12rem)"
        }}>
            <span className="block">Ready to</span>
            <span className="block">
              scale<em className="italic font-light text-primary">?</em>
            </span>
          </h2>

          {}
          <p className="font-default text-base sm:text-lg lg:text-xl text-muted-foreground max-w-2xl mb-12 sm:mb-16 leading-relaxed">Whether you need a high-converting website, a robust SEO strategy, or help fixing your Google Business Profile, Asahi Search is here to help you dominate your local market.</p>

          {}
          <div className="flex flex-col sm:flex-row items-center gap-6 sm:gap-10">
            <Button asChild size="lg" className="group h-16 sm:h-[64px] px-10 sm:px-12 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 text-base sm:text-lg font-medium tracking-wide transition-all duration-300 hover:scale-105 shadow-xl hover:shadow-2xl" style={{
            boxShadow: "0 0 40px hsla(200, 70%, 55%, 0.3), 0 0 80px hsla(330, 80%, 92%, 0.15)"
          }}>
              <Link to="/contact-us/">
                <span>Start Your Growth</span>
                <ArrowUpRight className="size-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </Link>
            </Button>

            <Link to="mailto:asahisearch@gmail.com" className="group inline-flex items-center gap-2 font-default text-base sm:text-lg text-foreground/80 hover:text-primary transition-colors duration-300">
              <span className="underline-offset-4 group-hover:underline">
                Or email our team
              </span>
              <ArrowUpRight className="size-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </div>
        </div>
      </div>

      {}
      <div className="absolute bottom-6 sm:bottom-10 left-6 sm:left-10 z-10 flex items-center gap-3 font-mono text-[10px] sm:text-xs tracking-[0.25em] uppercase text-muted-foreground">
        <span className="inline-block w-2 h-2 rounded-lg bg-primary" style={{
        animation: "ember-pulse 2s ease-in-out infinite"
      }} aria-hidden="true" />
        <span className="hidden xs:inline sm:inline">Accepting New Clients &mdash; 2026</span>
        <span className="xs:hidden sm:hidden">2026</span>
      </div>

      {}
      <div className="absolute bottom-6 sm:bottom-10 right-6 sm:right-10 z-10 font-mono text-[10px] sm:text-xs tracking-[0.3em] uppercase text-muted-foreground" style={{
      writingMode: "vertical-rl",
      transform: "rotate(180deg)"
    }}>
        ASAHI SEARCH 2026
      </div>
    </section>;
}